import React, { useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';

import { colour } from './tokens';

/**
 * A filter on a column heading, the way a spreadsheet does it.
 *
 * The filters used to be a row of a dozen selects above the table, which is a lot to read before
 * you have asked anything — and it put the question ("receipted?") a long way from the column
 * that answers it. Here the control is on the heading it belongs to, and a column with nothing
 * set looks like a plain heading.
 *
 * Only the columns the server can actually narrow get one. An icon on a column that did nothing
 * would be worse than no icon at all.
 */

export const YES_NO_OPTIONS = [
    { value: '', label: 'Any' },
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
];

const HeaderFilter = ({ label, value, options, onChange }) => {
    const [anchor, setAnchor] = useState(null);
    const active = String(value ?? '') !== '';
    const chosen = options.find((option) => option.value === value);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
            <Typography
                component="span"
                sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: active ? colour.primaryDeep : 'inherit',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {label}
            </Typography>

            <IconButton
                size="small"
                // The name says the column and what it is set to, so the control is findable and
                // readable without seeing the icon's colour.
                aria-label={active ? `${label} filter: ${chosen?.label ?? value}` : `Filter ${label}`}
                onClick={(event) => {
                    event.stopPropagation();
                    setAnchor(event.currentTarget);
                }}
                sx={{ p: 0.25, color: active ? colour.primary : colour.faint }}
            >
                {active ? <FilterAltIcon sx={{ fontSize: 15 }} /> : <FilterAltOutlinedIcon sx={{ fontSize: 15 }} />}
            </IconButton>

            <Menu
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                MenuListProps={{ dense: true }}
            >
                {options.map((option) => (
                    <MenuItem
                        key={option.value || 'any'}
                        selected={option.value === value}
                        onClick={() => {
                            onChange(option.value);
                            setAnchor(null);
                        }}
                        sx={{ fontSize: '13px' }}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};

/**
 * Builds the `renderHeader` for a column.
 *
 * The grid still needs `headerName` for the column list and the export, so this only changes how
 * the heading is drawn.
 */
export const filterableHeader = ({ label, value, options, onChange }) => () => (
    <HeaderFilter label={label} value={value} options={options} onChange={onChange} />
);

export default HeaderFilter;
